import { useState, useRef, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSSEConnected } from '../../hooks/useSSE'
import { useAuth } from '../../context/AuthContext'
import { fetchMessages, fetchConversation, fetchPersonas, createConversation } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import MagicBox from '../../components/MagicBox'
import ChatterBox from '../../components/ChatterBox'
import ChatterPlayer from '../../components/ChatterPlayer'
import { timeAgo } from '../../hooks/useTimeAgo'
import { useAwakePersonas, isAwake as checkAwake } from '../../hooks/useAwakePersonas'
import { usePersonaStatusMap } from '../../hooks/usePersonaStatusMap'
import { usePersonaPopup } from '../../components/PersonaPopup'
import type { Conversation, Message, Persona, Participant } from '../../lib/types'

// External persona as returned by GET /api/consulate/external-personas.
// Schema locked in msg 21912 (Harbor↔Forge) and shipped server-side in the
// Forge patch for same endpoint. Renders in the "External Communities"
// section of the sidebar; click opens a read-only-to-author / normal-to-us
// 1:1 with the persona via createConversation(gathering_user_id).
export interface ExternalPersona {
  persona_id: string
  name: string
  display_name: string
  community_id: string
  community_name: string
  gathering_user_id: string
  status: string
  persona_stream_heartbeat: string | null
  persona_send_activity: string | null
  paired_since: string | null
  key_prefix: string
  avatar_color: string
  avatar_letter: string
}

// ─── Persona Sidebar (collapsible, like Magic Box pattern) ───
function PersonaSidebar({ personas, awakeSet, externalPersonas, statusMap, onClickPersona, onClickHome, onClickExternalPersona }: {
  personas: Persona[]
  awakeSet: Set<string>
  externalPersonas?: ExternalPersona[]
  statusMap?: Record<string, { awake: boolean; contextPct: number | null; level: string; queueCount: number; taskCount: number }>
  onClickPersona: (name: string) => void
  onClickHome: (name: string) => void
  onClickExternalPersona?: (gatheringUserId: string, displayName: string) => void
}) {
  const [minimized, _setMinimized] = useState(() => localStorage.getItem('persona-sidebar-min') === '1')
  const setMinimized = (val: boolean) => { _setMinimized(val); localStorage.setItem('persona-sidebar-min', val ? '1' : '0') }

  if (minimized) {
    return (
      <div
        onClick={() => setMinimized(false)}
        className="shrink-0 flex items-center justify-center cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
        style={{ width: 24, borderRight: '1px solid var(--border)', background: 'var(--bg-body)', writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        title="Expand Personas"
      >
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)', letterSpacing: 2 }}>👥</span>
      </div>
    )
  }

  // Deduplicate personas by name
  const seen = new Set<string>()
  const unique = personas.filter(p => {
    if (seen.has(p.name)) return false
    seen.add(p.name)
    return true
  })

  const sorted = [...unique].sort((a, b) => {
    const aAwake = checkAwake(awakeSet, a.name) ? 1 : 0
    const bAwake = checkAwake(awakeSet, b.name) ? 1 : 0
    if (aAwake !== bAwake) return bAwake - aAwake
    return (a.display_name || a.name).localeCompare(b.display_name || b.name)
  })

  return (
    <div className="shrink-0 flex flex-col" style={{ width: 220, borderRight: '1px solid var(--border)', background: 'var(--bg-body)' }}>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)', letterSpacing: '0.1em' }}>Personas</span>
        <button onClick={() => setMinimized(true)} className="text-xs" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }} title="Minimize">◀</button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {sorted.map(p => {
          const isAwake = checkAwake(awakeSet, p.name)
          return (
            <div
              key={p.name}
              onClick={() => onClickPersona(p.name)}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--bg-elevated)] transition-colors"
              style={{ cursor: 'pointer' }}
              title={`Chat with ${p.display_name || p.name}`}
            >
              <div className="relative shrink-0">
                <PersonaAvatar name={p.name} size={24} />
                {isAwake && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ background: '#22c55e', border: '2px solid var(--bg-body)' }} />
                )}
              </div>
              <span
                className="text-xs flex-1 truncate"
                style={{ color: isAwake ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: isAwake ? 600 : 400 }}
              >
                {p.display_name || p.name}
              </span>
              {(() => {
                const status = statusMap?.[p.name.toLowerCase()]
                if (!status?.awake) return null
                const pct = status.contextPct
                if (pct == null) return null
                const color = pct >= 80 ? '#ef4444' : pct >= 70 ? '#f59e0b' : pct >= 60 ? '#eab308' : '#22c55e'
                return (
                  <span
                    className="text-xs font-mono shrink-0"
                    style={{ color, fontSize: 9, opacity: 0.9 }}
                    title={`${pct}% context used${status.queueCount ? ` · ${status.queueCount} queued` : ''}${status.taskCount ? ` · ${status.taskCount} tasks` : ''}`}
                  >
                    {pct}%
                  </span>
                )
              })()}
              <button
                onClick={(e) => { e.stopPropagation(); onClickHome(p.name) }}
                className="text-xs shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--text-muted)' }}
                title={`${p.display_name || p.name}'s profile`}
              >
                🏠
              </button>
            </div>
          )
        })}
        {externalPersonas && externalPersonas.length > 0 && onClickExternalPersona && (
          <ExternalCommunitiesList
            personas={externalPersonas}
            onClickPersona={onClickExternalPersona}
          />
        )}
      </div>
    </div>
  )
}

// "External Communities" section rendered at the bottom of the internal
// persona list. Groups paired external personas by community_name and
// opens a 1:1 by gathering_user_id when clicked (routed through the
// parent's handleExternalPersonaChat). Liveness dot uses the server-side
// persona_stream_heartbeat — within 90s counts as awake, same threshold
// Forge's /api/consulate/personas uses for internal personas.
function ExternalCommunitiesList({ personas, onClickPersona }: {
  personas: ExternalPersona[]
  onClickPersona: (gatheringUserId: string, displayName: string) => void
}) {
  // Group by community_id (stable unique key), preserving server
  // ORDER BY community_name, name. display_name carried alongside for
  // the section header. Using community_id as the React key avoids
  // collision when two external communities happen to share a name
  // (F5 — Sentinel).
  const grouped = useMemo(() => {
    const map = new Map<string, { displayName: string; members: ExternalPersona[] }>()
    for (const p of personas) {
      const id = p.community_id || p.community_name || 'external'
      const existing = map.get(id)
      if (existing) {
        existing.members.push(p)
      } else {
        map.set(id, {
          displayName: p.community_name || p.community_id || 'External',
          members: [p],
        })
      }
    }
    return Array.from(map.entries()) // [communityId, {displayName, members}][]
  }, [personas])

  const isAwake = (p: ExternalPersona): boolean => {
    if (!p.persona_stream_heartbeat) return false
    const hb = new Date(p.persona_stream_heartbeat).getTime()
    return !Number.isNaN(hb) && Date.now() - hb < 90_000
  }

  return (
    <div className="mt-3" style={{ borderTop: '1px solid var(--border)' }}>
      <div
        className="px-3 py-2"
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        External Communities
      </div>
      {grouped.map(([communityId, group]) => (
        <div key={communityId}>
          <div
            className="px-3 py-1"
            style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}
          >
            {group.displayName}
          </div>
          {group.members.map(p => {
            const awake = isAwake(p)
            // F1 belt-and-braces — server is authoritative (PALETTE), but
            // gate client-side too so a future server regression cannot
            // inject arbitrary CSS into the inline background.
            const safeColor = /^#[0-9a-fA-F]{6}$/.test(p.avatar_color) ? p.avatar_color : '#999999'
            return (
              <div
                key={p.persona_id}
                onClick={() => onClickPersona(p.gathering_user_id, p.display_name)}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--bg-elevated)] transition-colors"
                style={{ cursor: 'pointer' }}
                title={`Chat with ${p.display_name} (${group.displayName})`}
              >
                <div className="relative shrink-0">
                  <div
                    className="flex items-center justify-center rounded-full"
                    style={{
                      width: 24,
                      height: 24,
                      background: safeColor,
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {p.avatar_letter}
                  </div>
                  {awake && (
                    <div
                      className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
                      style={{ background: '#22c55e', border: '2px solid var(--bg-body)' }}
                    />
                  )}
                </div>
                <span
                  className="text-xs flex-1 truncate"
                  style={{
                    color: awake ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontWeight: awake ? 600 : 400,
                  }}
                >
                  {p.display_name}
                </span>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

const CHIP_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', joel: '#59C5FA', glean: '#A89060', wrench: '#8B8B8B',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', gretchen: '#B87DB8', vigil: '#708090', herald: '#C0785A',
  grain: '#A5886F', loom: '#7B6FA0', noma: '#E8A87C', counsel: '#6B7B8D',
  pulse: '#60A0B0', cadence: '#9B7A6C', anvil: '#7D7D7D', rivet: '#8C9440',
}
function chipColor(name: string): string { return CHIP_COLORS[name?.toLowerCase()] || '#8b949e' }
function displayName(name: string, fallbackDisplayName?: string): string {
  if (!name) return fallbackDisplayName?.charAt(0).toUpperCase() + (fallbackDisplayName?.slice(1) || '') || '?'
  if (name === 'joel') return 'Joel'
  if (name.startsWith('pu-')) {
    const rest = name.slice(3)
    // Long numeric PU IDs (e.g. pu-1774279550933-0918) — use display_name if available
    if (/^\d/.test(rest) || rest.length > 14) {
      if (fallbackDisplayName && !fallbackDisplayName.startsWith('pu-')) {
        return fallbackDisplayName.charAt(0).toUpperCase() + fallbackDisplayName.slice(1)
      }
      return 'User'
    }
    // Short named IDs like "pu-mira" → "Mira"
    const parts = rest.split('-')
    return parts[0][0].toUpperCase() + parts[0].slice(1)
  }
  return name[0].toUpperCase() + name.slice(1)
}

async function sendCommunityMessage(convId: string, content: string, sender: string) {
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const res = await fetch(`/api/conversations/${convId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ content, sender }),
  })
  if (!res.ok) {
    // Surface the server's actual error message instead of a generic "Send failed".
    // Hetzner returns JSON like {error: "Not a participant"} — extract and show that.
    let errMsg = ''
    try {
      const parsed = await res.json()
      errMsg = parsed?.error || ''
    } catch {
      errMsg = await res.text().catch(() => '')
    }
    // Friendlier message for the most common cases
    let friendly = errMsg
    if (res.status === 403 && /not a participant/i.test(errMsg)) {
      friendly = `You're not a participant in this conversation, so you can't send messages here. (As an admin you can see it, but you'd need to be added as a participant to write.)`
    } else if (res.status === 401) {
      friendly = `Your session expired. Please sign in again.`
    } else if (!friendly) {
      friendly = `Server returned HTTP ${res.status}`
    }
    alert(friendly)
    throw new Error(`Send failed (${res.status}): ${errMsg}`)
  }
  return res.json()
}


// ─── Thread Card ─────────────────────────────────────────
function ThreadCard({ convo, userName, userId, onOpen, onPin, onArchive, awakeSet = new Set<string>() }: {
  convo: Conversation
  userName: string
  userId: string
  onOpen: (c: Conversation) => void
  onPin: (id: string, pin: boolean) => void
  onArchive: (id: string, archive: boolean) => void
  awakeSet?: Set<string>
}) {
  const [hovering, setHovering] = useState(false)
  const others = (convo.participants || []).filter(p => {
    const id = (p.user_id || p.persona || '').toLowerCase()
    return id !== userName && id !== userId && id !== `pu-${userName}` && id !== 'joel'
  })
  const firstOther = others[0]?.persona || others[0]?.user_id || ''
  const color = chipColor(firstOther)
  const title = convo.title || others.map(p => displayName(p.persona || p.user_id || '', p.display_name)).join(', ') || 'Untitled'
  const preview = convo.last_message_preview || convo.last_message || ''
  const sender = convo.last_message_by || convo.last_sender || ''
  const isPinned = !!convo.is_pinned
  const isArchived = !!convo.archived_at
  const isGroup = convo.type === 'group'
  const unread = convo.unread_count || 0

  return (
    <div
      onClick={() => onOpen(convo)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
      style={{
        background: hovering ? 'var(--bg-elevated)' : 'transparent',
        borderLeft: `3px solid ${hovering ? 'var(--text-muted)' : 'transparent'}`,
      }}
    >
      {/* Avatar */}
      {isGroup ? (
        <div className="flex -space-x-1.5 shrink-0 mt-0.5">
          {others.slice(0, 3).map(p => (
            <PersonaAvatar key={p.user_id || p.persona} name={p.persona || p.user_id || ''} size={20} />
          ))}
        </div>
      ) : (
        <PersonaAvatar name={firstOther} size={36} />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 11 }} title={convo.room_type || 'chat'}>
            {convo.room_type === 'round-table' ? '⚔️' : convo.room_type === 'ceremony' ? '🔮' : convo.room_type === 'workspace' ? '📋' : isGroup ? '👥' : '💬'}
          </span>
          {isPinned && <span style={{ fontSize: 10 }}>📌</span>}
          <span
            className="text-sm font-semibold truncate"
            style={{ color: isGroup ? 'var(--text-primary)' : color }}
          >
            {title}
          </span>
          {isGroup && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', fontSize: 10 }}>
              {convo.participants?.length || 0}
            </span>
          )}
        </div>
        {preview && (
          <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {sender ? `${displayName(sender)}: ` : ''}{preview.substring(0, 100)}
          </div>
        )}
        {isGroup && others.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {others.slice(0, 5).map(p => {
              const c = chipColor(p.persona || p.user_id || '')
              const pRaw = (p.persona || p.user_id || '').toLowerCase()
              const isAwake = checkAwake(awakeSet, pRaw)
              return (
                <span
                  key={p.user_id || p.persona}
                  className="text-xs px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                  style={{ background: c + '18', color: c, fontSize: 9, fontWeight: 600 }}
                >
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: isAwake ? '#2ea043' : '#6e7681', flexShrink: 0 }} />
                  {displayName(p.persona || p.user_id || '', p.display_name)}
                </span>
              )
            })}
            {others.length > 5 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', fontSize: 9 }}>
                +{others.length - 5}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        {convo.last_message_at && (
          <div className="flex items-center gap-1">
            <span className="text-xs" style={{ color: 'var(--text-muted)', fontSize: 10 }}>{timeAgo(convo.last_message_at)}</span>
            {(() => {
              if (!isGroup && firstOther) {
                const nameKey = firstOther.toLowerCase().replace(/^pu-/, '')
                const personaAwake = checkAwake(awakeSet, nameKey)
                return <span style={{ width: 6, height: 6, borderRadius: '50%', display: 'inline-block', background: personaAwake ? '#2ea043' : '#6e7681' }} title={personaAwake ? 'Awake' : 'Sleeping'} />
              }
              const anyAwake = isGroup && others.some(p => {
                const n = (p.persona || p.user_id || '').toLowerCase().replace(/^pu-/, '')
                return checkAwake(awakeSet, n)
              })
              return <span style={{ width: 6, height: 6, borderRadius: '50%', display: 'inline-block', background: anyAwake ? '#2ea043' : '#6e7681' }} title={anyAwake ? 'Active participants' : 'All sleeping'} />
            })()}
          </div>
        )}
        {unread > 0 && (
          <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: color, color: '#fff', fontSize: 10, minWidth: 18, textAlign: 'center' }}>
            {unread}
          </span>
        )}
      </div>

      {/* Hover actions */}
      {hovering && (
        <div className="flex gap-1 shrink-0 ml-1" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onPin(convo.id, !isPinned)}
            className="text-xs px-1.5 py-0.5 rounded"
            style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 10 }}
          >
            {isPinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            onClick={() => onArchive(convo.id, !isArchived)}
            className="text-xs px-1.5 py-0.5 rounded"
            style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 10 }}
          >
            {isArchived ? 'Unarchive' : 'Archive'}
          </button>
        </div>
      )}
    </div>
  )
}

// ═══ Settings Panel (parity with ConversationViewPage) ═══
function InlineSettingsPanel({ convo, convId, participants, senderId, onClose, onSave }: {
  convo: Conversation | undefined
  convId: string
  participants: Participant[]
  senderId: string
  onClose: () => void
  onSave: () => void
}) {
  const [title, setTitle] = useState(convo?.title || '')
  const [visibility, setVisibility] = useState(convo?.visibility || 'open')
  const [roomType, setRoomType] = useState(convo?.room_type || 'chat')
  const [lead, setLead] = useState(convo?.metadata_lead || '')
  const [protocol, setProtocol] = useState(convo?.metadata_protocol || 'open-floor')
  const [chatter, setChatter] = useState(!!convo?.metadata_chatter)
  const [special, setSpecial] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addName, setAddName] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [allPersonas, setAllPersonas] = useState<{ name: string; user_id: string }[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  const adminIds: string[] = (() => {
    try { return JSON.parse(convo?.admin_ids || '[]') } catch { return [] }
  })()
  const isAdmin = adminIds.length === 0 || adminIds.includes(senderId)

  useEffect(() => {
    const tkn = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1] || ''
    let communityId = ''
    try { const p = JSON.parse(atob((tkn || '').split('.')[1] || 'e30=')); communityId = p.communityId || '' } catch {}
    const personaUrl = communityId && communityId !== 'portal-gathering' ? `/api/communities/${communityId}/personas` : '/api/personas'
    fetch(personaUrl, { headers: tkn ? { Authorization: `Bearer ${tkn}` } : {}, credentials: 'include' }).then(r => r.json()).then(data => {
      const list = (Array.isArray(data) ? data : data.personas || []).map((p: any) => ({
        name: p.display_name || p.name || p.id,
        user_id: p.user_id || `pu-${p.name || p.id}`,
      }))
      if (!list.find((p: any) => p.name === 'Joel')) list.unshift({ name: 'Joel', user_id: 'joel' })
      setAllPersonas(list)
    }).catch(() => {})
  }, [])

  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const authHeaders: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({
          title: title.trim() || null,
          visibility,
          room_type: roomType,
          metadata_lead: lead || null,
          metadata_protocol: protocol,
          metadata_chatter: chatter ? 1 : 0,
          metadata_special: special || null,
        }),
      })
      if (!res.ok) {
        setError(`Save failed (${res.status}) — nothing was changed. Try again.`)
        setSaving(false)
        return
      }
    } catch {
      setError('Save failed (network) — nothing was changed. Try again.')
      setSaving(false)
      return
    }
    setSaving(false)
    onSave()
  }

  async function handleAddMember(userId: string) {
    setAddLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}/add-member`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ user_id: userId, actor_id: senderId }),
      })
      if (!res.ok) {
        setError(`Add member failed (${res.status}) — not added. Try again.`)
        setAddLoading(false)
        return
      }
    } catch {
      setError('Add member failed (network) — not added. Try again.')
      setAddLoading(false)
      return
    }
    setAddName('')
    setShowSuggestions(false)
    setAddLoading(false)
    onSave()
  }

  async function handleRemoveMember(userId: string) {
    setError(null)
    try {
      const res = await fetch(`/api/conversations/${convId}/remove-member`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ user_id: userId, actor_id: senderId }),
      })
      if (!res.ok) {
        setError(`Remove member failed (${res.status}) — not removed. Try again.`)
        return
      }
    } catch {
      setError('Remove member failed (network) — not removed. Try again.')
      return
    }
    onSave()
  }

  const participantIds = new Set(participants.map(p => p.user_id))
  const suggestions = allPersonas.filter(p =>
    !participantIds.has(p.user_id) &&
    p.name.toLowerCase().includes(addName.toLowerCase())
  ).slice(0, 8)

  const inputStyle = {
    width: '100%', background: 'var(--bg-body)', border: '1px solid var(--border)',
    borderRadius: 4, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none',
  }
  const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', display: 'block' as const, marginBottom: 4 }

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div
        className="fixed z-50 rounded-xl p-5 overflow-y-auto"
        style={{
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          minWidth: 380, maxWidth: '90vw', maxHeight: '85vh',
        }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Conversation Settings</h3>

        <label style={labelStyle}>Title</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Auto-generated from participants..." style={{ ...inputStyle, marginBottom: 12 }} />

        <label style={labelStyle}>Visibility</label>
        <select value={visibility} onChange={e => setVisibility(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="open">Open (all community users)</option>
          <option value="participants">Participants only</option>
          <option value="custom">Custom (participants + allowed list)</option>
        </select>

        <label style={labelStyle}>Room Type</label>
        <select value={roomType} onChange={e => setRoomType(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="chat">Chat</option>
          <option value="ceremony">Ceremony</option>
          <option value="workspace">Workspace</option>
          <option value="3d">3D</option>
        </select>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Participants</h3>

        <div style={{ marginBottom: 12 }}>
          {participants.map(p => {
            const uid = p.user_id || p.persona
            const isParticipantAdmin = adminIds.includes(uid)
            const label = displayName(p.persona || p.user_id || '', p.display_name)
            return (
              <div key={uid} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ marginBottom: 2, background: 'var(--bg-body)' }}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
                  {isParticipantAdmin && (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(210,153,34,0.15)', color: '#d29922', fontSize: 10 }}>Admin</span>
                  )}
                </div>
                {isAdmin && uid !== senderId && (
                  <button
                    onClick={() => handleRemoveMember(uid)}
                    className="text-xs px-2 py-0.5 rounded"
                    style={{ background: 'rgba(248,81,73,0.1)', color: '#f85149', border: '1px solid rgba(248,81,73,0.2)', cursor: 'pointer', fontSize: 10 }}
                  >
                    Remove
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {isAdmin && (
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input
              type="text"
              value={addName}
              onChange={e => { setAddName(e.target.value); setShowSuggestions(true) }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Add participant..."
              disabled={addLoading}
              style={inputStyle}
            />
            {showSuggestions && addName.length > 0 && suggestions.length > 0 && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 4, marginTop: 2, maxHeight: 200, overflowY: 'auto',
                }}
              >
                {suggestions.map(s => (
                  <div
                    key={s.user_id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                    style={{ fontSize: 12, color: 'var(--text-primary)' }}
                    onMouseDown={() => handleAddMember(s.user_id)}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span>{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Conversation Rules</h3>

        <label style={labelStyle}>Lead</label>
        <select value={lead} onChange={e => setLead(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="">None</option>
          {participants.map(p => (
            <option key={p.user_id} value={p.persona || p.user_id}>{displayName(p.persona || p.user_id || '', p.display_name)}</option>
          ))}
        </select>

        <label style={labelStyle}>Protocol</label>
        <select value={protocol} onChange={e => setProtocol(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
          <option value="open-floor">Open Floor</option>
          <option value="synthesize">Synthesize (lead summarizes, others add only if new)</option>
          <option value="round-robin">Round Robin</option>
          <option value="ceremony">Ceremony</option>
        </select>

        <label style={labelStyle}>Special Instructions</label>
        <textarea
          value={special}
          onChange={e => setSpecial(e.target.value)}
          rows={3}
          placeholder="Custom rules for this conversation..."
          style={{ ...inputStyle, marginBottom: 12, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Voice</h3>

        <div className="flex items-center justify-between mb-3">
          <label style={{ ...labelStyle, margin: 0 }}>Chatter (voice presence)</label>
          <button
            onClick={() => setChatter(!chatter)}
            className="text-xs px-3 py-1 rounded-full"
            style={{
              background: chatter ? 'rgba(63,185,80,0.15)' : 'var(--bg-elevated)',
              color: chatter ? 'var(--success)' : 'var(--text-muted)',
              border: chatter ? '1px solid rgba(63,185,80,0.3)' : '1px solid var(--border)',
              cursor: 'pointer',
            }}
          >
            {chatter ? 'ON' : 'OFF'}
          </button>
        </div>

        {error && (
          <div className="text-xs mt-3 px-3 py-2 rounded" style={{ background: 'rgba(248,81,73,0.12)', color: '#f85149', border: '1px solid rgba(248,81,73,0.35)' }}>
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="text-xs px-4 py-1.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="text-xs px-4 py-1.5 rounded font-medium" style={{ background: 'var(--success)', color: '#fff', border: 'none', cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  )
}

export default function CommunityConversationsPage() {
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const { hoverProps } = usePersonaPopup()
  const personaStatusMap = usePersonaStatusMap()
  const communityId = user?.communityId || `portal-${user?.communitySlug || ''}`
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  const [activeConvo, _setActiveConvo] = useState<Conversation | null>(null)
  const setActiveConvo = (c: Conversation | null) => {
    if (c && (c.room_type === 'round-table' || c.room_type === 'circle' || c.room_type === 'workspace' || c.room_type === 'ceremony')) {
      window.location.href = `/conversation/${c.id}`
      return
    }
    _setActiveConvo(c)
  }
  const [showSettings, setShowSettings] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  // Drafts are keyed per-conversation. The visible draft is whatever belongs
  // to the current activeConvo — never carries across conversations.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const draft = activeConvo ? (drafts[activeConvo.id] || '') : ''
  const setDraft = (val: string) => {
    if (!activeConvo) return
    setDrafts(prev => ({ ...prev, [activeConvo.id]: val }))
  }
  const [autoScroll, setAutoScroll] = useState(true)
  const programmaticScrollRef = useRef(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const awakeSet = useAwakePersonas()

  // Force refetch conversations on every page mount (fixes circles disappearing on SPA navigation)
  // Use refetchQueries instead of invalidateQueries to avoid clearing cached data during refetch
  useEffect(() => {
    queryClient.refetchQueries({ queryKey: ['communityConversations'] })
  }, [])

  // Filter state
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [newConvTitle, setNewConvTitle] = useState('')
  const [newConvParticipants, setNewConvParticipants] = useState<string[]>([])
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('talk-collapsed-sections')
    if (saved) { try { return new Set(JSON.parse(saved)) } catch {} }
    return new Set(['The Table', 'Research', 'Engineering', 'KoralMind', 'Agora', 'Biz Dev', 'Personal'])
  })

  // Fetch personas — community users get their community's persona list directly
  const isCommunity = user?.userType === 'community'
  const { data: allPersonas } = useQuery({
    queryKey: ['personas', isCommunity ? communityId : 'internal'],
    queryFn: async () => {
      if (isCommunity && communityId) {
        const res = await fetch(`/api/communities/${communityId}/personas`, { headers, credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          const list = Array.isArray(data) ? data : (data.personas || [])
          if (list.length > 0) return list
        }
      }
      // Fallback: fetch from status endpoint (unauthenticated, auto-syncs from Mini)
      try {
        const statusRes = await fetch('/api/personas/status')
        if (statusRes.ok) {
          const statusData = await statusRes.json()
          if (statusData && typeof statusData === 'object' && !Array.isArray(statusData)) {
            return Object.keys(statusData).sort().map(name => ({ name, display_name: name.charAt(0).toUpperCase() + name.slice(1) }))
          }
        }
      } catch {}
      return fetchPersonas()
    },
  })
  const personas = Array.isArray(allPersonas) ? allPersonas : []

  // Fetch paired external personas for the sidebar "External Communities"
  // section (Diff C). Schema locked to the 13 fields returned by Forge's
  // /api/consulate/external-personas endpoint (Harbor↔Forge msg 21912,
  // Sentinel-signed). Refetches every 60s so awake dots and new pairings
  // reflect without a page reload. Failures silently render no section.
  const { data: externalPersonas } = useQuery<ExternalPersona[]>({
    queryKey: ['externalPersonas', communityId],
    queryFn: async () => {
      const res = await fetch('/api/consulate/external-personas', {
        credentials: 'include',
        headers,
      })
      if (!res.ok) {
        // F6 — surface auth/server regressions in devtools so an empty
        // list doesn't mask a 401/403/500. UI still renders empty, but
        // the developer sees the cause on next reload.
        console.warn('[externalPersonas] non-OK response', res.status, res.statusText)
        return []
      }
      const data = await res.json()
      return Array.isArray(data) ? data : []
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })

  // Create conversation mutation — include current user as participant
  const currentUserName = user?.loginName || user?.userName || ''
  const createMut = useMutation({
    mutationFn: (params: { selected: string[]; title: string }) => {
      const allParticipants = currentUserName && !params.selected.includes(currentUserName)
        ? [currentUserName, ...params.selected]
        : params.selected
      return createConversation(allParticipants, params.title, communityId)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
      setShowCreate(false)
      // Open the newly created conversation
      if (data?.conversation) setActiveConvo(data.conversation)
    },
  })

  // Fetch conversations
  // NOTE: shares query key with CommunityLobbyPage — both must return { conversations: [...] }
  const { data: convData, isLoading } = useQuery({
    queryKey: ['communityConversations', communityId],
    queryFn: async () => {
      const res = await fetch('/api/conversations?limit=5000', { headers })
      if (!res.ok) throw new Error(`conversations fetch failed: ${res.status}`)
      const data = await res.json()
      const all = Array.isArray(data) ? data : (data.conversations || [])
      const userName = user?.loginName?.toLowerCase() || user?.userName?.toLowerCase() || ''
      const communitySlug = user?.communitySlug || ''
      const convs = all
        .filter((c: Record<string, unknown>) => {
          if (c.community_id === communityId) return true
          if (c.community_id === `portal-${communitySlug}`) return true
          const parts = ((c.participants || []) as any[]).map((p: any) => (p.user_id || p || '').toLowerCase())
          if (userName && (parts.includes(userName) || parts.includes(`pu-${userName}`) || parts.includes(`pu-${userName}-gathering`) || parts.some(id => id.includes(userName)))) return true
          return false
        })
        .map((c: Record<string, unknown>) => {
          if (c.participants && Array.isArray(c.participants) && c.participants.length && typeof c.participants[0] === 'string') {
            c.participants = (c.participants as string[]).map(p => ({ persona: p }))
          }
          return c
        }) as Conversation[]
      return { conversations: convs }
    },
    enabled: !!communityId,
    refetchInterval: sseOk ? false : 30_000,
    staleTime: 0,
    placeholderData: (prev: { conversations: Conversation[] } | undefined) => prev,
  })
  const conversations = convData?.conversations || []

  // Fetch messages for active conversation
  const { data: msgData } = useQuery({
    queryKey: ['messages', activeConvo?.id],
    queryFn: () => fetchMessages(activeConvo!.id),
    enabled: !!activeConvo,
    refetchInterval: sseOk ? false : 15_000, // SSE-conditional polling fallback
  })

  // Fetch full conversation detail for metadata (chatter, lead, protocol)
  const { data: convoDetail } = useQuery({
    queryKey: ['conversation', activeConvo?.id],
    queryFn: () => fetchConversation(activeConvo!.id),
    enabled: !!activeConvo,
    refetchInterval: sseOk ? false : 30_000,
  })

  // Jitsi room name for video calls
  const jitsiRoomName = activeConvo ? `gathering-${activeConvo.id.replace(/[^a-zA-Z0-9]/g, '')}` : ''

  const sendMut = useMutation({
    mutationFn: (content: string) => sendCommunityMessage(activeConvo!.id, content, user?.userId || user?.loginName || user?.userName || 'client'),
    // Optimistic clear: empty the box the instant they hit send, so a slow/jittery
    // connection can't make it look "stuck" (which caused accidental re-sends).
    // The content is already captured in the mutation variable, so clearing is safe.
    onMutate: () => {
      setDraft('')
      // After send, the user wants to see their new message — force autoScroll back on.
      setAutoScroll(true)
    },
    onError: (_err, content) => {
      // Send failed — restore exactly what they typed so nothing is lost.
      setDraft(content)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', activeConvo?.id] })
      setAutoScroll(true)
    },
  })

  const messages = msgData?.messages || []
  const msgCount = useMemo(() => messages.length, [messages.length])
  const prevMsgCountRef = useRef(0)

  const isClientMessage = (msg: Message) => {
    const sender = msg.sender?.toLowerCase()
    return sender === user?.loginName?.toLowerCase() || sender === user?.userName?.toLowerCase() || sender === 'joel'
  }

  // Track which conversation we've already done the initial scroll-to-bottom for.
  // Using a ref (not state) so we don't cause extra renders, and it persists across
  // all message count changes for the same conversation.
  const initialScrolledConvIdRef = useRef<string | null>(null)

  // Helper: robust scroll-to-bottom that retries across frames until the scrollHeight
  // stops changing (MarkdownRenderer + many messages take multiple layout passes).
  const forceScrollToBottom = (): (() => void) => {
    let cancelled = false
    let lastHeight = -1
    let stableCount = 0

    const tick = () => {
      if (cancelled) return
      const endRef = messagesEndRef.current
      if (!endRef) {
        requestAnimationFrame(tick)
        return
      }
      let container: HTMLElement | null = endRef.parentElement
      while (container && container !== document.body) {
        const overflow = getComputedStyle(container).overflowY
        if (overflow === 'auto' || overflow === 'scroll') break
        container = container.parentElement
      }
      if (!container) return

      programmaticScrollRef.current = true
      container.scrollTop = container.scrollHeight

      if (container.scrollHeight === lastHeight) {
        stableCount++
      } else {
        stableCount = 0
        lastHeight = container.scrollHeight
      }

      // Keep retrying for up to ~1 second or until height stabilizes for 3 frames
      if (stableCount < 3) {
        requestAnimationFrame(tick)
      } else {
        setTimeout(() => { programmaticScrollRef.current = false }, 100)
      }
    }
    requestAnimationFrame(tick)
    return () => { cancelled = true }
  }

  // Incremental scroll: when new messages arrive in an already-open conversation,
  // scroll to the new message (if user hasn't manually scrolled up). Uses the same
  // retry-loop helper as initial scroll because a single scrollIntoView fires before
  // layout settles for messages with markdown content, leaving the new bubble hidden
  // behind the input box.
  useEffect(() => {
    if (msgCount > prevMsgCountRef.current && autoScroll && initialScrolledConvIdRef.current === activeConvo?.id) {
      forceScrollToBottom()
    }
    prevMsgCountRef.current = msgCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgCount, autoScroll, activeConvo?.id])

  // When activeConvo changes, reset scroll tracking + mark as read.
  useEffect(() => {
    // Always clear the initial-scroll marker when activeConvo changes (including
    // when becoming null via Back) so re-opening the same conversation counts as
    // a fresh open and re-triggers the scroll-to-bottom effect.
    initialScrolledConvIdRef.current = null
    setShowSettings(false)
    setShowVideo(false)
    if (activeConvo) {
      prevMsgCountRef.current = 0
      setAutoScroll(true)

      setTimeout(() => inputRef.current?.focus(), 100)
      const sender = user?.userId || user?.loginName || user?.userName
      if (sender) {
        fetch(`/api/conversations/${activeConvo.id}/read`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ persona: sender }),
        }).catch(() => {})
      }
    }
  }, [activeConvo])

  // Initial scroll-to-bottom: fires ONCE per conversation, as soon as msgCount > 0.
  // Runs independently of the incremental scroll effect so they don't fight.
  useEffect(() => {
    if (!activeConvo || msgCount === 0) return
    if (initialScrolledConvIdRef.current === activeConvo.id) return
    initialScrolledConvIdRef.current = activeConvo.id
    const cleanup = forceScrollToBottom()
    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConvo?.id, msgCount])

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    if (programmaticScrollRef.current) return
    const el = e.currentTarget
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 100)
  }

  function handleSend() {
    if (!draft.trim()) return
    sendMut.mutate(draft.trim())
  }

  // Pin / Archive actions
  async function handlePin(convId: string, pin: boolean) {
    await fetch(`/api/conversations/${convId}/${pin ? 'pin' : 'unpin'}`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
    })
    queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
  }

  async function handleArchive(convId: string, archive: boolean) {
    await fetch(`/api/conversations/${convId}/${archive ? 'archive' : 'unarchive'}`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
    })
    queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
  }

  // Handler: click persona name → find or create 1:1 chat
  // Tightened: matches an existing 1:1 only when the participant set is EXACTLY
  // {self, persona}. Falls back to server-side find_or_create for the create path,
  // and always reads back the full conversation object so setActiveConvo fires reliably.
  const handlePersonaChat = async (personaName: string) => {
    const personaLower = personaName.toLowerCase()
    const selfNames = [
      user?.loginName?.toLowerCase(),
      user?.userName?.toLowerCase(),
      user?.userId?.toLowerCase(),
      'joel',
    ].filter(Boolean) as string[]

    const isSelfId = (id: string) => selfNames.includes(id.toLowerCase())

    // Strict find: a 1:1 whose participant set is exactly {self, persona}.
    // PU-numbers are already filtered out by the server for display purposes,
    // so a real 1:1 Joel↔persona comes back with 1-2 non-self entries.
    const existing = conversations.find(c => {
      if (c.type !== '1:1') return false
      const parts = (c.participants || []).map(p =>
        (p.persona || p.user_id || p.display_name || '').toLowerCase()
      )
      const nonSelf = parts.filter(id => !isSelfId(id))
      // Exactly one non-self participant, and it must be the target persona
      return nonSelf.length === 1 && nonSelf[0] === personaLower
    })

    if (existing) {
      setActiveConvo(existing)
      return
    }

    // Create (or server finds) a new 1:1 with this persona.
    // Defense-in-depth: the server's find_or_create has historically returned
    // group chats that happen to contain both participants. Assert the returned
    // conversation is actually a 1:1 before activating; otherwise retry fresh.
    const currentUserName = user?.userId || user?.loginName || user?.userName || ''
    const isValid1to1 = (c: Conversation | undefined) => {
      if (!c) return false
      if (c.type !== '1:1') return false
      const parts = (c.participants || []).map(p =>
        (p.persona || p.user_id || p.display_name || '').toLowerCase()
      )
      const nonSelf = parts.filter(id => !isSelfId(id))
      return parts.length <= 2 && nonSelf.length === 1 && nonSelf[0] === personaLower
    }
    const result = await createConversation(
      currentUserName ? [currentUserName, personaName] : [personaName],
      '',
      communityId
    )
    if (isValid1to1(result?.conversation ?? undefined)) {
      queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
      setActiveConvo(result.conversation)
      return
    }
    // Server returned a wrongly-typed conversation (group or mismatched).
    // Don't silently land the user in a group chat — log and show nothing
    // rather than hiding the bug under a wrong-route landing.
    console.error('[PersonaChat] find_or_create returned non-1:1', {
      requested: [currentUserName, personaName],
      received: result?.conversation,
    })
    alert(`Couldn't open a 1:1 with ${personaName}. The server returned a conversation that isn't a proper 1:1 — this has been logged.`)
  }

  // Open a 1:1 with a paired external persona. Uses the persona's
  // gathering_user_id (pu-*) rather than a local slug, because the
  // external persona exists as a first-class user row in community_users
  // and the conversation's participant identity is the pu-* id.
  const handleExternalPersonaChat = async (gatheringUserId: string, displayName: string) => {
    const targetLower = gatheringUserId.toLowerCase()
    const selfNames = [
      user?.loginName?.toLowerCase(),
      user?.userName?.toLowerCase(),
      user?.userId?.toLowerCase(),
      'joel',
    ].filter(Boolean) as string[]
    const isSelfId = (id: string) => selfNames.includes(id.toLowerCase())

    // Reuse the existing 1:1 with this external persona if one exists
    // (avoids duplicate conversation rows when the user clicks twice).
    const existing = conversations.find(c => {
      if (c.type !== '1:1') return false
      const parts = (c.participants || []).map(p =>
        (p.persona || p.user_id || p.display_name || '').toLowerCase()
      )
      const nonSelf = parts.filter(id => !isSelfId(id))
      return nonSelf.length === 1 && nonSelf[0] === targetLower
    })
    if (existing) {
      setActiveConvo(existing)
      return
    }

    const myId = currentUserName || user?.userId || ''
    const result = await createConversation(
      myId ? [myId, gatheringUserId] : [gatheringUserId],
      '',
      communityId
    )
    if (result?.conversation) {
      queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
      setActiveConvo(result.conversation)
      return
    }
    console.error('[ExternalPersonaChat] createConversation returned no conversation', { gatheringUserId, displayName })
  }

  const handlePersonaHome = (name: string) => {
    window.location.href = `/neighborhood/${name}`
  }

  // ═══ CHAT VIEW ═══
  if (activeConvo) {
    return (
      <div className="flex h-full">
        <PersonaSidebar personas={personas || []} awakeSet={awakeSet} externalPersonas={externalPersonas} statusMap={personaStatusMap} onClickPersona={handlePersonaChat} onClickHome={handlePersonaHome} onClickExternalPersona={handleExternalPersonaChat} />
        <div className="flex-1 flex flex-col min-w-0">
        {/* Chat header */}
        <div className="shrink-0 flex items-center gap-3 px-6 py-3" style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
          <button
            onClick={() => setActiveConvo(null)}
            className="text-sm font-medium"
            style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}
          >
            ← Back
          </button>
          <div className="flex -space-x-1">
            {(activeConvo.participants || []).slice(0, 4).map(p => (
              <PersonaAvatar key={p.user_id} name={p.persona || p.user_id || ''} size={22} />
            ))}
          </div>
          {(() => {
            // 1:1 conversations always render as "{Persona} / {Viewer}" —
            // never "Conversation", never a stored custom title. 1:1s are not
            // renameable by design (the relationship IS the name).
            const isOneOnOne = activeConvo.type === '1:1' && (activeConvo.participants || []).length <= 2
            if (isOneOnOne) {
              const viewerName = user?.loginName || user?.userName || 'You'
              const other = (activeConvo.participants || []).find(p => {
                const id = (p.persona || p.user_id || p.display_name || '').toLowerCase()
                return !['joel', viewerName.toLowerCase(), user?.userId?.toLowerCase()].filter(Boolean).includes(id)
              })
              const personaLabel = displayName(other?.persona || other?.user_id || 'Persona', other?.display_name)
              const viewerLabel = displayName(viewerName)
              return (
                <span className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                  {personaLabel} / {viewerLabel}
                </span>
              )
            }
            return (
              <span className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                {activeConvo.title || 'Conversation'}
              </span>
            )
          })()}
          {(() => {
            const statusMap = personaStatusMap
            const personaParticipants = (activeConvo.participants || [])
              .map(p => (p.persona || p.user_id || '').toLowerCase())
              .filter(name => statusMap[name]?.awake && statusMap[name]?.contextPct != null)
            if (personaParticipants.length === 0) return null
            return (
              <div className="flex items-center gap-1.5">
                {personaParticipants.map(name => {
                  const pct = statusMap[name]?.contextPct ?? 0
                  const color = pct >= 80 ? '#ef4444' : pct >= 70 ? '#f59e0b' : pct >= 60 ? '#eab308' : '#22c55e'
                  const shouldPulse = pct >= 60
                  return (
                    <span
                      key={name}
                      className="text-xs px-1.5 py-0.5 rounded-full font-mono"
                      style={{
                        background: `${color}18`,
                        color,
                        border: `1px solid ${color}40`,
                        animation: shouldPulse ? 'token-pulse 2s ease-in-out infinite' : 'none',
                      }}
                      title={`${name}: ${pct}% context used`}
                    >
                      {pct}%
                    </span>
                  )
                })}
              </div>
            )
          })()}
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {(activeConvo.participants || []).length} participants
          </span>
          <div className="flex items-center gap-2">
            {convoDetail?.metadata_lead && typeof convoDetail.metadata_lead === 'string' && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                Lead: {displayName(convoDetail.metadata_lead)}
              </span>
            )}
            {convoDetail?.metadata_chatter ? (
              <span className="text-xs" style={{ color: 'var(--success)' }} title="Chatter is ON">🔊</span>
            ) : null}
            <button
              onClick={() => setShowVideo(v => !v)}
              className="text-xs px-1.5 py-1 rounded"
              style={{
                color: showVideo ? '#34d399' : 'var(--text-muted)',
                background: showVideo ? 'rgba(52,211,153,0.12)' : 'transparent',
                border: 'none', cursor: 'pointer',
              }}
              title={showVideo ? 'End video call' : 'Start video call'}
            >
              📹
            </button>
            <button
              onClick={() => setShowSettings(s => !s)}
              className="text-xs px-1.5 py-1 rounded"
              style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              title="Settings"
            >
              ⚙
            </button>
          </div>
        </div>

        {/* Settings Panel Overlay */}
        {showSettings && (
          <InlineSettingsPanel
            convo={convoDetail || activeConvo}
            convId={activeConvo.id}
            participants={convoDetail?.participants || activeConvo.participants || []}
            senderId={user?.userId || ''}
            onClose={() => setShowSettings(false)}
            onSave={() => {
              queryClient.invalidateQueries({ queryKey: ['conversation', activeConvo.id] })
              queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
              setShowSettings(false)
            }}
          />
        )}

        {/* Content area (Video + Magic Box + Messages + ChatterBox) */}
        <div className="flex-1 flex overflow-hidden">

          {/* Video Call Panel */}
          {showVideo && jitsiRoomName && (
            <div
              style={{
                width: '40%',
                flexShrink: 0,
                borderRight: '1px solid var(--border)',
                background: '#000',
                position: 'relative',
              }}
            >
              <iframe
                src={`https://meet.thegathering.space/${jitsiRoomName}#config.prejoinConfig.enabled=false&config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false&config.enableLobbyChat=false&config.requireDisplayName=false&config.lobbyModeEnabled=false&config.disableThirdPartyRequests=true&config.disableProfile=true&config.filmstrip.disabled=true&config.participantsPane.enabled=false&config.notifications=[]&interfaceConfig.SHOW_JITSI_WATERMARK=false&interfaceConfig.FILM_STRIP_MAX_HEIGHT=0&interfaceConfig.VERTICAL_FILMSTRIP=false&interfaceConfig.TOOLBAR_BUTTONS=["microphone","camera","desktop","raisehand","hangup"]&userInfo.displayName=${encodeURIComponent(user?.loginName || user?.userName || 'Guest')}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                allowFullScreen
              />
            </div>
          )}

          <MagicBox conversationId={activeConvo.id} />
          <div className="flex-1 min-w-0 overflow-y-auto" ref={scrollContainerRef} onScroll={handleScroll}>
            <div className="px-6 py-4">
              <div className="max-w-2xl mx-auto flex flex-col gap-2">
                {messages.map(msg => {
                  const isClient = isClientMessage(msg)
                  return (
                    <div key={msg.id} data-msg-id={msg.id} className={`flex gap-2.5 ${isClient ? 'flex-row-reverse' : ''}`}>
                      <PersonaAvatar name={msg.sender} size={32} />
                      <div
                        className="max-w-[75%] px-4 py-3 text-sm leading-relaxed"
                        style={{
                          borderRadius: isClient ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: isClient ? 'var(--accent)' : 'var(--bg-surface)',
                          color: isClient ? '#0A1628' : 'var(--text-primary)',
                          border: isClient ? 'none' : '1px solid var(--border)',
                        }}
                      >
                        {!isClient && (
                          <div className="text-xs font-semibold mb-1" {...hoverProps(msg.sender)} style={{ cursor: 'pointer', color: 'var(--accent)' }}>
                            {msg.sender}
                          </div>
                        )}
                        <MarkdownRenderer content={msg.content} />
                        <div className="text-xs mt-1.5" style={{ opacity: 0.5 }}>
                          {timeAgo(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {/* Chatter Box — right panel for chatter entries */}
          {activeConvo.id && !!convoDetail?.metadata_chatter && (
            <ChatterBox
              conversationId={activeConvo.id}
              onScrollToMessage={(msgId) => {
                const el = scrollContainerRef.current?.querySelector(`[data-msg-id="${msgId}"]`)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  const div = el as HTMLElement
                  div.style.background = 'var(--bg-elevated)'
                  setTimeout(() => { div.style.background = '' }, 2000)
                }
              }}
            />
          )}
        </div>

        {/* Input — below content area so all panels end at the same line */}
        <div className="shrink-0 px-6 py-3" style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border)' }}>
          <div className="max-w-2xl mx-auto flex gap-2">
            <textarea
              ref={inputRef as any}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
              onFocus={() => {
                setAutoScroll(true)
                forceScrollToBottom()
              }}
              placeholder="Type a message..."
              rows={3}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm outline-none resize-none"
              style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            />
            <button
              onClick={handleSend}
              disabled={!draft.trim() || sendMut.isPending}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: (!draft.trim() || sendMut.isPending) ? 'var(--bg-elevated)' : 'var(--accent)',
                color: (!draft.trim() || sendMut.isPending) ? 'var(--text-muted)' : '#fff',
                border: '1px solid var(--border)',
                opacity: (!draft.trim() || sendMut.isPending) ? 0.5 : 1,
                cursor: (!draft.trim() || sendMut.isPending) ? 'not-allowed' : 'pointer',
              }}
            >
              {sendMut.isPending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>

        {/* Chatter audio player */}
        {activeConvo.id && !!convoDetail?.metadata_chatter && (
          <ChatterPlayer conversationId={activeConvo.id} chatterEnabled={true} />
        )}
      </div>
      </div>
    )
  }

  // ═══ LIST VIEW — Four-Tier Layout (Joel directive, Apr 18) ═══
  const userName = user?.loginName?.toLowerCase() || user?.userName?.toLowerCase() || ''
  const userId = user?.userId?.toLowerCase() || ''
  const allConvos = conversations || []

  const isSelf = (id: string) => {
    const lower = id.toLowerCase()
    return lower === userName || lower === userId || lower === `pu-${userName}` || lower === 'joel'
  }

  const getTime = (c: Conversation) => {
    const ts = c.last_message_at || c.updated_at
    if (!ts) return 0
    const t = new Date(ts).getTime()
    return isNaN(t) ? 0 : t
  }
  const sortByRecent = (a: Conversation, b: Conversation) => getTime(b) - getTime(a)

  // Four-tier classification
  const circleRooms = allConvos.filter(c => c.room_type === 'circle' && !c.archived_at)
  const roundTable = allConvos.find(c => c.room_type === 'round-table' && !c.archived_at)

  const circleOrder = ['Research Circle', 'Centaur Studios', 'Agora Circle', 'KoralMind Circle', 'Business Dev Circle']
  circleRooms.sort((a, b) => {
    const ai = circleOrder.indexOf(a.title || '')
    const bi = circleOrder.indexOf(b.title || '')
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  const circleAndTableIds = new Set([...circleRooms.map(c => c.id), roundTable?.id].filter(Boolean))

  const projectChats = allConvos.filter(c =>
    c.project_id && !c.archived_at && !circleAndTableIds.has(c.id) &&
    c.room_type !== 'ceremony' && c.type !== 'ceremony' &&
    ((c.message_count ?? 0) > 0 || c.last_message)
  ).sort(sortByRecent)

  const isGroup = (c: Conversation) => c.type === 'group' || ((c.participants || []).filter(p => !isSelf(p.user_id || p.persona || '')).length > 1)

  const otherConvos = allConvos.filter(c =>
    !c.archived_at && !circleAndTableIds.has(c.id) && !c.project_id &&
    c.room_type !== 'ceremony' && c.type !== 'ceremony' &&
    ((c.message_count ?? 0) > 0 || c.last_message)
  )

  const groupChats = otherConvos.filter(c => isGroup(c)).sort(sortByRecent)
  const oneOnOnes = otherConvos.filter(c => !isGroup(c)).sort(sortByRecent)

  // Search filter
  const matchesSearch = (c: Conversation) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (c.title || '').toLowerCase().includes(q) ||
      (c.participants || []).some(p => (p.persona || p.user_id || '').toLowerCase().includes(q) || (p.display_name || '').toLowerCase().includes(q)) ||
      (c.last_message_preview || c.last_message || '').toLowerCase().includes(q)
  }

  const archivedCount = allConvos.filter(c => c.archived_at).length

  const CIRCLE_COLORS: Record<string, string> = {
    'Research': '#9C59F5', 'Engineering': '#B25943', 'Community': '#4E937A',
    'KoralMind': '#33C7A0', 'Biz Dev': '#5AA0D0', 'Round Table': '#E8A63A',
  }
  const CIRCLE_CENTERS: Record<string, string> = {
    'Research': 'mira', 'Engineering': 'tack', 'Community': 'griot', 'KoralMind': 'flux', 'Biz Dev': 'vector',
  }
  function circleNameFromTitle(title: string): string {
    if (title.includes('Research')) return 'Research'
    if (title.includes('Centaur') || title.includes('Engineering')) return 'Engineering'
    if (title.includes('Agora') || title.includes('Community')) return 'Community'
    if (title.includes('KoralMind')) return 'KoralMind'
    if (title.includes('Biz') || title.includes('Business')) return 'Biz Dev'
    return title
  }

  const toggleSection = (name: string) => setCollapsedSections(prev => {
    const next = new Set(prev)
    if (next.has(name)) next.delete(name); else next.add(name)
    localStorage.setItem('talk-collapsed-sections', JSON.stringify([...next]))
    return next
  })

  return (
    <div className="flex h-full">
      <PersonaSidebar personas={personas || []} awakeSet={awakeSet} externalPersonas={externalPersonas} onClickPersona={handlePersonaChat} onClickHome={handlePersonaHome} onClickExternalPersona={handleExternalPersonaChat} />
      <div className="flex-1 min-w-0 px-6 py-5 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Talk</h1>
        <div className="flex items-center gap-3">
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..." className="px-3 py-1.5 rounded-lg text-xs outline-none"
            style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', width: 200 }}
          />
          <button onClick={() => setShowCreate(true)} className="text-xs px-3 py-1.5 rounded-md"
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}>
            + New Conversation
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <div>
          {/* ═══ TIER 1: Circle Chats ═══ */}
          <div className="mb-6">
            <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)', letterSpacing: 2 }}>
              Circles
            </div>
            <div className="flex flex-wrap gap-3">
              {circleRooms.map(c => {
                const cn = circleNameFromTitle(c.title || '')
                const color = CIRCLE_COLORS[cn] || '#E8A63A'
                const center = CIRCLE_CENTERS[cn]
                const preview = c.last_message || c.last_message_preview || ''
                const sender = c.last_sender || c.last_message_by || ''
                const lastAt = c.last_message_at
                const dot = !lastAt ? '#6e7681' : Date.now() - new Date(lastAt).getTime() < 86400000 ? '#2ea043' : Date.now() - new Date(lastAt).getTime() < 604800000 ? '#d29922' : '#6e7681'
                return (
                  <div key={c.id} onClick={() => setActiveConvo(c)}
                    className="rounded-xl cursor-pointer transition-all hover:scale-[1.02]"
                    style={{ background: 'var(--bg-surface)', border: `1px solid ${color}33`, padding: '14px 16px', minWidth: 200, flex: '1 1 180px', maxWidth: 280 }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{cn}</span>
                      {(c.unread_count ?? 0) > 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full text-white ml-auto" style={{ background: color, fontSize: 9 }}>{c.unread_count}</span>
                      )}
                    </div>
                    {center && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <PersonaAvatar name={center} size={18} showStatus awake={checkAwake(awakeSet, center)} />
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{displayName(center)} · Center</span>
                        {(() => {
                          const status = personaStatusMap[center.toLowerCase()]
                          if (!status?.awake || status.contextPct == null) return null
                          const pct = status.contextPct
                          const pColor = pct >= 80 ? '#ef4444' : pct >= 70 ? '#f59e0b' : pct >= 60 ? '#eab308' : '#22c55e'
                          return (
                            <span className="text-xs font-mono" style={{ color: pColor, fontSize: 9 }} title={`${pct}% context`}>
                              {pct}%
                            </span>
                          )
                        })()}
                      </div>
                    )}
                    {preview ? (
                      <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {sender && <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{displayName(sender)}: </span>}
                        {preview.slice(0, 80)}
                      </div>
                    ) : (
                      <div className="text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>No messages yet</div>
                    )}
                    {lastAt && <div className="text-xs mt-1.5" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>{timeAgo(lastAt)}</div>}
                  </div>
                )
              })}
              {roundTable && (
                <div onClick={() => setActiveConvo(roundTable)}
                  className="rounded-xl cursor-pointer transition-all hover:scale-[1.02]"
                  style={{ background: 'var(--bg-surface)', border: '1px solid #E8A63A33', padding: '14px 16px', minWidth: 200, flex: '1 1 180px', maxWidth: 280 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: roundTable.last_message_at && Date.now() - new Date(roundTable.last_message_at).getTime() < 86400000 ? '#2ea043' : '#6e7681' }} />
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#E8A63A' }}>⚔️ Round Table</span>
                    {(roundTable.unread_count ?? 0) > 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full text-white ml-auto" style={{ background: '#E8A63A', fontSize: 9 }}>{roundTable.unread_count}</span>
                    )}
                  </div>
                  {(roundTable.last_message || roundTable.last_message_preview) ? (
                    <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                      {(roundTable.last_sender || roundTable.last_message_by) && <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{displayName(roundTable.last_sender || roundTable.last_message_by || '')}: </span>}
                      {(roundTable.last_message || roundTable.last_message_preview || '').slice(0, 80)}
                    </div>
                  ) : (
                    <div className="text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>No messages yet</div>
                  )}
                  {roundTable.last_message_at && <div className="text-xs mt-1.5" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>{timeAgo(roundTable.last_message_at)}</div>}
                </div>
              )}
            </div>
          </div>

          {/* ═══ TIER 2: Project Chats ═══ */}
          {projectChats.filter(matchesSearch).length > 0 && (
            <div className="mb-5">
              <button onClick={() => toggleSection('projects')} className="flex items-center gap-2 mb-2 w-full text-left"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{collapsedSections.has('projects') ? '▸' : '▾'}</span>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Project Chats</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>{projectChats.filter(matchesSearch).length}</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)', marginLeft: 8 }} />
              </button>
              {!collapsedSections.has('projects') && (
                <div className="flex flex-col">
                  {projectChats.filter(matchesSearch).map(c => (
                    <ThreadCard key={c.id} convo={c} userName={userName} userId={userId} onOpen={setActiveConvo} onPin={handlePin} onArchive={handleArchive} awakeSet={awakeSet} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══ TIER 3: Group Chats ═══ */}
          {groupChats.filter(matchesSearch).length > 0 && (
            <div className="mb-5">
              <button onClick={() => toggleSection('groups')} className="flex items-center gap-2 mb-2 w-full text-left"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{collapsedSections.has('groups') ? '▸' : '▾'}</span>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Group Chats</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>{groupChats.filter(matchesSearch).length}</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)', marginLeft: 8 }} />
              </button>
              {!collapsedSections.has('groups') && (
                <div className="flex flex-col">
                  {groupChats.filter(matchesSearch).map(c => (
                    <ThreadCard key={c.id} convo={c} userName={userName} userId={userId} onOpen={setActiveConvo} onPin={handlePin} onArchive={handleArchive} awakeSet={awakeSet} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══ TIER 4: One-on-Ones ═══ */}
          {oneOnOnes.filter(matchesSearch).length > 0 && (
            <div className="mb-5">
              <button onClick={() => toggleSection('people')} className="flex items-center gap-2 mb-2 w-full text-left"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{collapsedSections.has('people') ? '▸' : '▾'}</span>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>One-on-One</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>{oneOnOnes.filter(matchesSearch).length}</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)', marginLeft: 8 }} />
              </button>
              {!collapsedSections.has('people') && (
                <div className="flex flex-col">
                  {oneOnOnes.filter(matchesSearch).map(c => (
                    <ThreadCard key={c.id} convo={c} userName={userName} userId={userId} onOpen={setActiveConvo} onPin={handlePin} onArchive={handleArchive} awakeSet={awakeSet} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archived */}
          {archivedCount > 0 && (
            <div className="mt-2">
              <button onClick={() => setShowArchived(!showArchived)} className="text-xs"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                {showArchived ? '▾' : '▸'} Archived ({archivedCount})
              </button>
            </div>
          )}
        </div>
      )}

      {/* New Conversation Modal */}
      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowCreate(false)}>
          <div className="rounded-xl p-6 w-full max-w-md" style={{ background: 'var(--bg-surface, var(--surface))', border: '1px solid var(--border)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>New Conversation</h3>
            <input
              type="text" placeholder="Conversation name (optional for 1:1)" value={newConvTitle}
              onChange={e => setNewConvTitle(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-sm mb-4 outline-none"
              style={{ background: 'var(--bg, var(--bg-body))', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              autoFocus
            />
            <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Add participants</div>
            <div className="flex flex-wrap gap-2 mb-4 max-h-48 overflow-y-auto">
              {(personas || []).map((p: { name: string; display_name?: string }) => {
                const isSelected = newConvParticipants.includes(p.name)
                const col = chipColor(p.name)
                return (
                  <button key={p.name}
                    onClick={() => setNewConvParticipants(prev => prev.includes(p.name) ? prev.filter(n => n !== p.name) : [...prev, p.name])}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs"
                    style={{ background: isSelected ? col + '22' : 'transparent', color: isSelected ? col : 'var(--text-secondary)', border: `1px solid ${isSelected ? col + '66' : 'var(--border)'}`, cursor: 'pointer' }}>
                    <PersonaAvatar name={p.name} size={18} />
                    {p.display_name || p.name}
                  </button>
                )
              })}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="text-xs px-4 py-2 rounded-lg"
                style={{ color: 'var(--text-secondary)', background: 'none', border: '1px solid var(--border)', cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={() => { if (newConvParticipants.length > 0) { createMut.mutate({ selected: newConvParticipants, title: newConvTitle.trim() || newConvParticipants.join(' & ') }); setNewConvParticipants([]); setNewConvTitle('') } }}
                disabled={newConvParticipants.length === 0}
                className="text-xs px-4 py-2 rounded-lg disabled:opacity-40"
                style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}>
                {newConvParticipants.length > 1 ? 'Create Group' : 'Start Chat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  )
}
